import { useEffect, useMemo, useState } from 'react';
import { format, parseISO, addDays, startOfDay } from 'date-fns';
import { useTranslations } from '@/i18n';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { cn } from '@/lib/utils';

interface TripExtendDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newEndDate: Date) => void;
  currentEndDate: string; // ISO date string
  isLoading?: boolean;
  errorMessage?: string;
}

export default function TripExtendDialog({
  isOpen,
  onClose,
  onConfirm,
  currentEndDate,
  isLoading = false,
  errorMessage,
}: TripExtendDialogProps) {
  const t = useTranslations('trips');
  const tCommon = useTranslations('common');

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Parse the current end date and calculate minimum selectable date (current end date + 1 day)
  const parsedCurrentEndDate = useMemo(() => {
    if (!currentEndDate) return null;
    const parsed = parseISO(currentEndDate);
    return Number.isNaN(parsed.getTime()) ? null : startOfDay(parsed);
  }, [currentEndDate]);

  const minSelectableDate = useMemo(() => {
    if (!parsedCurrentEndDate) return startOfDay(new Date());
    return addDays(parsedCurrentEndDate, 1);
  }, [parsedCurrentEndDate]);

  // Disable dates before the minimum selectable date
  const disabledDates = useMemo(() => {
    return (date: Date) => {
      return startOfDay(date) < minSelectableDate;
    };
  }, [minSelectableDate]);

  const isSelectedDateValid = useMemo(() => {
    if (!selectedDate) {
      return false;
    }
    return startOfDay(selectedDate).getTime() >= minSelectableDate.getTime();
  }, [minSelectableDate, selectedDate]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedDate(null);
      setIsCalendarOpen(false);
      return;
    }
    if (selectedDate && !isSelectedDateValid) {
      setSelectedDate(null);
    }
  }, [isOpen, isSelectedDateValid, selectedDate]);

  const handleConfirm = () => {
    if (selectedDate && isSelectedDateValid) {
      onConfirm(selectedDate);
    }
  };

  const handleAttemptClose = () => {
    if (isLoading) {
      return;
    }
    setSelectedDate(null);
    setIsCalendarOpen(false);
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleAttemptClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-[25rem] gap-0 rounded-3xl border-0 p-0"
        showCloseButton={false}
      >
        {/* Hidden DialogTitle for accessibility */}
        <DialogTitle className="sr-only">{t('extendConfirmation.title')}</DialogTitle>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="text-[18px] leading-7 font-bold text-black">
            {t('extendConfirmation.title')}
          </h2>
          <Button
            onClick={handleAttemptClose}
            disabled={isLoading}
            variant="unstyled"
            size="none"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f1f1f1] transition-colors hover:bg-[#e4e4e4] disabled:opacity-50"
            aria-label="Close dialog"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
        </div>

        {/* Content */}
        <div className="px-6 pb-4">
          <p className="mb-4 text-base leading-6 text-black">
            {t('extendConfirmation.selectNewEndDate')}
          </p>

          {/* Current end date info */}
          {parsedCurrentEndDate && (
            <p className="mb-3 text-sm text-[#666666]">
              {t('extendConfirmation.currentEndDate')}:{' '}
              <span className="font-medium text-[#292929]">
                {format(parsedCurrentEndDate, 'dd/MM/yyyy')}
              </span>
            </p>
          )}

          {/* Date Picker */}
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={isLoading}
                className={cn(
                  'flex h-14 w-full cursor-pointer items-center justify-between rounded-2xl border border-[#ebebeb] px-4 py-3 transition-colors focus-within:border-[#292929] hover:border-[#292929]',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  !selectedDate && 'text-[#666666]'
                )}
              >
                <div className="flex items-center gap-3">
                  <img src="/assets/icons/calendar.svg" alt="" className="h-6 w-6" />
                  <span
                    className={cn('text-base', selectedDate ? 'text-[#292929]' : 'text-[#666666]')}
                  >
                    {selectedDate
                      ? format(selectedDate, 'dd/MM/yyyy')
                      : t('extendConfirmation.selectDate')}
                  </span>
                </div>
                <img src="/assets/icons/chevron-down.svg" alt="" className="h-6 w-6" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate ?? undefined}
                onSelect={(date) => {
                  setSelectedDate(date ?? null);
                  setIsCalendarOpen(false);
                }}
                captionLayout="dropdown"
                className="rounded-2xl border-[#ebebeb]"
                defaultMonth={minSelectableDate}
                disabled={disabledDates}
              />
            </PopoverContent>
          </Popover>

          {/* Error Message */}
          {errorMessage && (
            <div className="mt-3 rounded-2xl border border-[#ffecec] bg-[#fff6f6] px-4 py-3 text-sm leading-5 text-[#e5484d]">
              {errorMessage}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-2 px-6 pb-6">
          <Button
            type="button"
            variant="ghost"
            onClick={handleAttemptClose}
            disabled={isLoading}
            className="h-auto rounded-xl px-3.5 py-2.5 text-sm font-medium text-black hover:bg-[#f9f9f9] disabled:opacity-50"
          >
            {t('extendConfirmation.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading || !isSelectedDateValid}
            className="h-auto rounded-full px-4 py-3 text-[15px] font-medium text-white transition-colors hover:opacity-90 disabled:opacity-50"
            style={{
              background:
                'linear-gradient(90deg, #F1656A -66.86%, #ED2891 47.89%, #42419A 167.14%)',
            }}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <Spinner variant="circle" className="h-4 w-4" />
                <span>{tCommon('processing')}</span>
              </div>
            ) : (
              t('extendConfirmation.confirm')
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
